import type { Tool } from './types';

export const aiAstrologer: Tool = {
  slug: 'ai-astrologer',
  metaTitle: 'Talk to an AI Astrologer Online — Free AI Vedic Astrology Chat',
  metaDescription:
    'Chat with a free AI astrologer online, 24/7. Ask about career, marriage, health and timing — answered from your own Vedic birth chart using classical Jyotish, in plain language.',
  keywords:
    'ai astrologer, talk to ai astrologer, ai astrology chat, ai vedic astrology, ai jyotish, free ai astrologer online, chat with astrologer',
  eyebrow: 'AI Vedic Astrologer',
  h1: 'Talk to an AI Astrologer Online, Free',
  appName: 'KundaliAI AI Astrologer',
  intro:
    'Ask an AI Vedic astrologer anything — career, marriage, money, health or timing — and get answers grounded in your own birth chart, 24/7, in plain language. KundaliAI reads your Kundali the way a seasoned Jyotishi would, using classical Vedic rules. Free to start, no appointment needed.',
  ctaLabel: 'Start chatting free',
  ctaHref: '/chat',
  embed: 'chat',
  sections: [
    {
      heading: 'Free, open 24/7, no login',
      body: "Enter your name and date of birth above and ask your first question — there is no account to create and no waiting room. Your first five questions are free without login, and a free account adds ten more. Unlike call-an-astrologer apps there is no per-minute charge, and unlike a daily horoscope the answer is about *your* chart, not everyone born under your sign. If you already have a [free janam kundali](/free-kundli) from KundaliAI, the astrologer reads that same chart.",
    },
    {
      heading: 'What you can ask about',
      body: 'People use KundaliAI for:\n\n- **Career & finance** — job timing, business, wealth yogas.\n- **Marriage & relationships** — timing, [kundli matching](/kundli-matching), Mangal Dosha.\n- **Health & wellbeing** — chart indications and cautious periods.\n- **Dasha & transits** — what the current planetary period means for you.\n- **Remedies** — traditional measures suited to your chart.\n\nAsk *"when will I get a job?"*, *"is this a good year for marriage?"* or *"which Dasha am I running?"* and keep asking follow-ups; the conversation stays anchored to your chart.',
    },
    {
      heading: 'Answers come from your chart, not a generic horoscope',
      body: 'The AI first computes your birth chart from precise astronomical positions (the same ephemeris behind the free kundali, [checked to the minute against Drik Panchang](/accuracy)), then reasons over the real placements — houses, planetary lords, aspects, Nakshatras and Dasha periods — applying classical Jyotish rules. It explains its reasoning in everyday language so you understand *why*, not just *what*. What that does and does not make reliable is covered honestly in [Is AI astrology accurate?](/blog/is-ai-astrology-accurate)',
    },
    {
      heading: 'Ask in Hindi or English',
      body: 'The astrologer answers in Hindi, Marathi or English — switch the language from the menu and ask in the language you think in. Sanskrit terms (dasha, lagna, nakshatra) are kept where they are the precise word, and explained where they are not.',
    },
    {
      heading: 'How KundaliAI compares to KundliGPT and AstroSage AI',
      body: 'Many AI astrology chatbots give surface-level answers or cap you at two or three questions before asking for a card. KundaliAI stays anchored to your actual chart across the whole conversation, checks the classical conditions (like dosha cancellations) that simple tools miss, and lets you ask five questions with no login at all. Bring your birth details and see the difference.',
    },
  ],
  faq: [
    {
      question: 'Is the AI astrologer free without login?',
      answer:
        'Yes. Enter your name and date of birth and ask five questions without creating an account. A free account adds ten more and keeps your chart and conversation; premium plans unlock unlimited questions and detailed reports.',
    },
    {
      question: 'Can I talk to the AI astrologer in Hindi?',
      answer:
        'Yes. Choose Hindi (or Marathi) from the language menu and the astrologer answers in that language, using the standard Jyotish terms.',
    },
    {
      question: 'How is this different from a normal horoscope?',
      answer:
        'A daily horoscope is generic to your sun or moon sign. KundaliAI answers from your full birth chart and responds to your specific questions in a back-and-forth conversation.',
    },
    {
      question: 'Is AI astrology accurate?',
      answer:
        'The chart is computed from precise astronomical data, and the interpretation applies classical Jyotish rules. AI makes that expertise instant and conversational, though it is intended for guidance and reflection, not as a substitute for professional advice.',
    },
  ],
  related: [
    { label: 'Free Kundli by date of birth', href: '/free-kundli' },
    { label: 'Kundli matching by AI', href: '/kundli-matching' },
    { label: 'Mangal Dosha calculator', href: '/mangal-dosha-calculator' },
  ],
};
